import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, User, Settings, LogOut, Search, HelpCircle, Mail, Calendar, Plus, Cloud, MailQuestion as QuestionMark,
  BookOpen, Brush, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { MiniCalendar } from '../MiniCalendar';
import { ThemeToggle } from '../ThemeToggle';
import AvatarDisplay from '../AvatarDisplay';
import SubscriptionBadge from '../SubscriptionBadge';
import { NotificationsDropdown } from '../NotificationsDropdown';
import { HelpMenu } from '../HelpMenu';
import SearchDropdown from '../SearchDropdown';
import { supabase } from '../../lib/supabase';
import type { AvatarData } from '../AvatarGenerator';
import { toast } from 'react-hot-toast';

interface TopNavProps {
  isSideNavOpen: boolean;
  onMenuClick: () => void;
}

export const TopNav: React.FC<TopNavProps> = ({ isSideNavOpen, onMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [avatarData, setAvatarData] = useState<AvatarData | undefined>(undefined);
  const [profilePictureError, setProfilePictureError] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Show success message
      toast.success('Logged out successfully');
      
      // Redirect to login page
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to log out');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('Loading user profile for avatar display...');
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('avatar_data')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error loading profile:', profileError);
        return;
      }

      console.log('Profile data loaded:', data);
      if (data?.avatar_data) {
        console.log('Setting avatar data:', data.avatar_data);
        setAvatarData(data.avatar_data);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const notifications = [
    {
      id: 1,
      title: 'New Comment',
      message: 'Sarah left a comment on your post',
      time: '2 minutes ago',
    },
    {
      id: 2,
      title: 'Performance Update',
      message: 'Your latest post is performing well',
      time: '1 hour ago',
    },
    {
      id: 3,
      title: 'Reminder',
      message: 'You have a scheduled post tomorrow',
      time: '3 hours ago',
    },
  ];

  // Get user's name from Supabase auth
  const userName = JSON.parse(localStorage.getItem('sb-bqpssdvpdwxpsjhkdjoc-auth-token') || '{}')
    ?.user?.user_metadata?.full_name || 'User';

  // Handle keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to open search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset search when location changes
  useEffect(() => {
    setSearchTerm('');
    setShowSearch(false);
  }, [location]);

  const handleSearchFocus = () => {
    setShowSearch(true);
  };

  const handleSearchBlur = (e: React.FocusEvent) => {
    // Only close if not clicking on a search result
    if (!e.relatedTarget || !e.relatedTarget.closest('.search-results')) {
      setTimeout(() => {
        setShowSearch(false);
      }, 200);
    }
  };

  const handleSelectResult = (result: any) => {
    navigate(result.path);
    setSearchTerm('');
    setShowSearch(false);
  };
  return (
    <header className="fixed top-0 right-0 left-0 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 z-20 shadow-sm w-full max-w-[100vw]">
      <div className={`flex items-center justify-between h-16 px-2 sm:px-4 ${isSideNavOpen ? 'lg:ml-64' : 'lg:ml-20'} transition-all duration-300 w-auto`}>
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="p-1.5 sm:p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 z-50"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden md:block relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder="Search tools, pages, and features..."
                className="pl-9 pr-4 py-1.5 bg-gray-100 dark:bg-dark-700 border-0 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 w-64"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-dark-600 rounded text-xs text-gray-500 dark:text-gray-400">
                  Ctrl K
                </kbd>
              </div>
            </div>
            <SearchDropdown 
              isOpen={showSearch && searchInputRef.current === document.activeElement}
              onClose={() => setShowSearch(false)}
              searchTerm={searchTerm}
              onSelectResult={handleSelectResult}
            />
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-3">
          <div className="hidden md:flex items-center space-x-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowHelpMenu(true)}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <QuestionMark className="h-5 w-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <Mail className="h-5 w-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCalendar(!showCalendar)}
              className="relative p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <Calendar className="h-5 w-5" />
              <MiniCalendar isOpen={showCalendar} onClose={() => setShowCalendar(false)} />
            </motion.button>
          </div>

          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="p-1 sm:p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <Plus className="h-5 w-5" />
            </motion.button>

            <AnimatePresence>
              {showQuickActions && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowQuickActions(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                  >
                    <div className="p-2 space-y-1">
                      <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg">
                        <Plus className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                        New Post
                      </button>
                      <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg">
                        <Calendar className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                        Schedule Content
                      </button>
                      <button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg">
                        <Mail className="h-4 w-4 mr-2 text-indigo-600 dark:text-indigo-400" />
                        New Message
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <ThemeToggle />
          
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="p-1 sm:p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg relative"
            >
              <Bell className="h-5 w-5" />
              <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-dark-800" />
            </motion.button>
            
            <NotificationsDropdown
              isOpen={isNotificationsOpen}
              onClose={() => setIsNotificationsOpen(false)}
            />
          </div>
          
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <button
              onClick={() => navigate('/settings')}
              className="p-1 sm:p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            >
              <Settings className="h-5 w-5" />
            </button>
          </motion.div>
          
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center space-x-2"
            >
              <AvatarDisplay 
                avatarData={avatarData}
                size="md"
                fallbackText={userName}
                className="border-2 border-indigo-100 dark:border-indigo-800/30"
              />
              <div className="hidden lg:block text-left">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{userName}</div>
                <div className="flex items-center space-x-1">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Account</div>
                  <SubscriptionBadge size="sm" />
                </div>
              </div>
            </motion.button>

            <AnimatePresence>
              {showProfileMenu && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowProfileMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-64 bg-white dark:bg-dark-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                  >
                    <div className="p-2">
                      <div className="p-3 border-b border-gray-100 dark:border-dark-700">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full overflow-hidden">
                            <AvatarDisplay
                              avatarData={avatarData}
                              size="md"
                              fallbackText={userName}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{userName}</div>
                            <div className="flex items-center space-x-1">
                              <div className="text-xs text-gray-500 dark:text-gray-400">Account</div>
                              <SubscriptionBadge size="sm" />
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate('/settings');
                          }}
                          className="mt-3 flex items-center justify-center px-3 py-1.5 text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 cursor-pointer"
                        >
                          <Brush className="h-3.5 w-3.5 mr-1.5" />
                          Customize Avatar
                        </button>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            setShowLogoutConfirm(true);
                          }}
                          className="w-full text-left block px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <div className="flex items-center">
                            <LogOut className="h-4 w-4 mr-2" />
                            <span>Log Out</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      
      {/* Help Menu */}
      <HelpMenu isOpen={showHelpMenu} onClose={() => setShowHelpMenu(false)} />

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-50"
              onClick={() => !isLoggingOut && setShowLogoutConfirm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Log Out
                    </h3>
                    <button
                      onClick={() => !isLoggingOut && setShowLogoutConfirm(false)}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      disabled={isLoggingOut}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to log out? This will end your current session and you'll need to log in again to access your account.
                  </p>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => !isLoggingOut && setShowLogoutConfirm(false)}
                      disabled={isLoggingOut}
                      className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors flex items-center"
                    >
                      {isLoggingOut ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Logging out...
                        </>
                      ) : (
                        <>
                          <LogOut className="h-4 w-4 mr-2" />
                          Log Out
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};